/**
 * The spatial navigation rule, as pure geometry.
 *
 * Separated from `FocusContext` for one reason: this is the part that can be wrong in a
 * way nobody notices until they are holding a remote in front of a television. A rule
 * expressed over plain rectangles can be tested against a synthetic layout — a rail above
 * a grid, a sidebar beside both — and asserted to have no dead ends, which is exactly the
 * Phase 3 acceptance criterion. The same rule living inside a React context could only be
 * tested through a DOM that reports every rectangle as zero.
 *
 * ## The rule
 *
 * A candidate must be strictly in the direction pressed. Among those, the score is
 * distance along the axis of travel **plus a heavy penalty for misalignment across it**.
 *
 * The cross-axis weight is the difference between usable and maddening. With pure
 * distance, Down from the first card of a rail lands on whichever element happens to be
 * closest diagonally — often a sidebar item — rather than the card directly underneath.
 * Overlap across the axis zeroes the penalty entirely, so anything visually "in the same
 * column" beats anything merely nearby.
 */

/** How much harder misalignment across the axis of travel counts than distance along it. */
export const CROSS_AXIS_PENALTY = 4;

/**
 * Tiebreak among candidates that *are* aligned.
 *
 * Two cards can both overlap the source's column — a chip above a grid of narrower cards
 * overlaps two of them — and without this the winner would be whichever registered first,
 * which depends on mount order and therefore changes with an unrelated refactor. Small
 * enough that an aligned candidate always beats a misaligned one.
 */
const ALIGNED_BIAS = 0.25;

/**
 * How far outside the screen a candidate may sit and still be reachable, as a fraction
 * of the screen's own size.
 *
 * This is the fix for a real defect, worth recording because the symptom was baffling and
 * the first fix for it was wrong. A rail is `overflow-x: auto`, so its cards exist at
 * their true coordinates whether or not they are on screen. From the last card of the
 * first rail, Right found a card in the *second* rail — further right, several hundred
 * pixels lower, and a thousand pixels off the screen — because nothing else lay to the
 * right at all. Focus jumped two rails away and the page scrolled to follow it.
 *
 * The instinct is to make the scoring stricter, and it does not work: the offending
 * candidate is no worse aligned than a sidebar item is from a chip, so no angle or ratio
 * separates them. What separates them is that one is visible and the other is nowhere
 * near the screen — which is also the rule a viewer is actually using. You move to
 * something you can see.
 *
 * The margin is not zero because moving *is* what scrolls. Landing on the last visible
 * card of a rail puts it flush against the right edge, and the next card then begins just
 * past it; with a hard viewport test that card would be unreachable and the rail would
 * dead-end halfway along. A quarter of the screen is comfortably more than one card and
 * comfortably less than the distance to a different rail.
 */
const OFF_SCREEN_MARGIN = 0.25;

export interface Viewport {
  width: number;
  height: number;
}

/**
 * Whether a candidate is close enough to the screen to be worth moving to.
 *
 * Deliberately generous in the direction of "yes": a false negative is a dead end, which
 * is far worse than a focus move that scrolls a little further than expected.
 */
export function nearViewport(r: Rect, viewport: Viewport): boolean {
  const marginX = viewport.width * OFF_SCREEN_MARGIN;
  const marginY = viewport.height * OFF_SCREEN_MARGIN;
  return (
    r.right > -marginX &&
    r.left < viewport.width + marginX &&
    r.bottom > -marginY &&
    r.top < viewport.height + marginY
  );
}

export type Direction = 'left' | 'up' | 'right' | 'down';

export interface Rect {
  left: number;
  right: number;
  top: number;
  bottom: number;
  cx: number;
  cy: number;
}

export function rectOf(el: HTMLElement): Rect {
  const r = el.getBoundingClientRect();
  return {
    left: r.left,
    right: r.right,
    top: r.top,
    bottom: r.bottom,
    cx: r.left + r.width / 2,
    cy: r.top + r.height / 2,
  };
}

/** Builds a rect from a plain box. Used by tests and by anything without a DOM node. */
export function rect(left: number, top: number, width: number, height: number): Rect {
  return {
    left,
    top,
    right: left + width,
    bottom: top + height,
    cx: left + width / 2,
    cy: top + height / 2,
  };
}

/** Overlap of two 1-D spans; zero or negative means a gap. */
function overlap(aStart: number, aEnd: number, bStart: number, bEnd: number): number {
  return Math.min(aEnd, bEnd) - Math.max(aStart, bStart);
}

/**
 * Score for moving from `from` to `to`. Lower is better; null means "not that way".
 *
 * The direction test uses the leading edge rather than the centre, so a tall element
 * beside a short one is still reachable sideways.
 */
export function score(from: Rect, to: Rect, direction: Direction): number | null {
  /** Gap along the axis of travel; negative would mean the candidate is behind us. */
  let along: number;
  /** True when the two rectangles share any span across that axis — "same column". */
  let aligned: boolean;
  /** Centre-to-centre distance across the axis. */
  let offset: number;

  switch (direction) {
    case 'left':
      if (to.right > from.left) return null;
      along = from.left - to.right;
      aligned = overlap(from.top, from.bottom, to.top, to.bottom) > 0;
      offset = Math.abs(from.cy - to.cy);
      break;
    case 'right':
      if (to.left < from.right) return null;
      along = to.left - from.right;
      aligned = overlap(from.top, from.bottom, to.top, to.bottom) > 0;
      offset = Math.abs(from.cy - to.cy);
      break;
    case 'up':
      if (to.bottom > from.top) return null;
      along = from.top - to.bottom;
      aligned = overlap(from.left, from.right, to.left, to.right) > 0;
      offset = Math.abs(from.cx - to.cx);
      break;
    case 'down':
      if (to.top < from.bottom) return null;
      along = to.top - from.bottom;
      aligned = overlap(from.left, from.right, to.left, to.right) > 0;
      offset = Math.abs(from.cx - to.cx);
      break;
  }

  // Aligned wins outright, however far away — that is what makes the bottom of a long
  // grid column reachable from the top of it — with centre distance only as a tiebreak.
  return aligned ? along + offset * ALIGNED_BIAS : along + offset * CROSS_AXIS_PENALTY;
}

/**
 * Picks the best candidate in a direction, or null when there is nothing that way.
 *
 * Ties go to whichever candidate was offered first. That only happens between two
 * elements at identical distance *and* identical alignment, which in practice means they
 * overlap — at which point either answer is as good as the other, and a stable one is
 * better than a random one.
 */
export function bestCandidate<T extends { id: string; rect: Rect }>(
  from: Rect,
  candidates: readonly T[],
  direction: Direction,
  /** Omit to score on geometry alone — which is what the reachability tests want. */
  viewport?: Viewport,
): T | null {
  let best: T | null = null;
  let bestValue = Infinity;

  for (const candidate of candidates) {
    if (viewport && !nearViewport(candidate.rect, viewport)) continue;
    const value = score(from, candidate.rect, direction);
    if (value === null || value >= bestValue) continue;
    best = candidate;
    bestValue = value;
  }

  return best;
}
